/**
 * Normalize JPEG EXIF orientation into actual pixels so saved images
 * match what the browser preview shows (fixes 90° rotation after upload).
 */

function getExifOrientation(arrayBuffer) {
  const view = new DataView(arrayBuffer)
  if (view.byteLength < 2 || view.getUint16(0, false) !== 0xffd8) return 1

  let offset = 2
  while (offset + 4 < view.byteLength) {
    const marker = view.getUint16(offset, false)
    offset += 2
    if (marker === 0xffe1) {
      if (offset + 6 > view.byteLength) break
      if (view.getUint32(offset + 2, false) !== 0x45786966) break // "Exif"
      const tiffOffset = offset + 8
      if (tiffOffset + 8 > view.byteLength) break
      const little = view.getUint16(tiffOffset, false) === 0x4949
      const firstIFDOffset = view.getUint32(tiffOffset + 4, little)
      let entryOffset = tiffOffset + firstIFDOffset
      if (entryOffset + 2 > view.byteLength) break
      const tags = view.getUint16(entryOffset, little)
      entryOffset += 2
      for (let i = 0; i < tags; i += 1) {
        const tagOffset = entryOffset + i * 12
        if (tagOffset + 12 > view.byteLength) break
        if (view.getUint16(tagOffset, little) === 0x0112) {
          return view.getUint16(tagOffset + 8, little)
        }
      }
      break
    }
    if ((marker & 0xff00) !== 0xff00) break
    if (offset + 2 > view.byteLength) break
    offset += view.getUint16(offset, false)
  }
  return 1
}

function drawOrientedImage(img, orientation) {
  const canvas = document.createElement('canvas')
  const ctx = canvas.getContext('2d')
  const width = img.naturalWidth || img.width
  const height = img.naturalHeight || img.height

  if (orientation > 4 && orientation < 9) {
    canvas.width = height
    canvas.height = width
  } else {
    canvas.width = width
    canvas.height = height
  }

  switch (orientation) {
    case 2: ctx.transform(-1, 0, 0, 1, width, 0); break
    case 3: ctx.transform(-1, 0, 0, -1, width, height); break
    case 4: ctx.transform(1, 0, 0, -1, 0, height); break
    case 5: ctx.transform(0, 1, 1, 0, 0, 0); break
    case 6: ctx.transform(0, 1, -1, 0, height, 0); break
    case 7: ctx.transform(0, -1, -1, 0, height, width); break
    case 8: ctx.transform(0, -1, 1, 0, 0, width); break
    default: break
  }

  ctx.drawImage(img, 0, 0)
  return canvas.toDataURL('image/jpeg', 0.92)
}

function loadImageFromUrl(url) {
  return new Promise((resolve, reject) => {
    const img = new Image()
    img.onload = () => resolve(img)
    img.onerror = reject
    img.src = url
  })
}

function readAsDataUrl(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = e => resolve(e.target.result)
    reader.onerror = reject
    reader.readAsDataURL(file)
  })
}

function readAsArrayBuffer(file) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = e => resolve(e.target.result)
    reader.onerror = reject
    reader.readAsArrayBuffer(file)
  })
}

async function normalizeWithCreateImageBitmap(file) {
  if (typeof createImageBitmap !== 'function') return null
  try {
    const bitmap = await createImageBitmap(file, { imageOrientation: 'from-image' })
    const canvas = document.createElement('canvas')
    canvas.width = bitmap.width
    canvas.height = bitmap.height
    const ctx = canvas.getContext('2d')
    ctx.drawImage(bitmap, 0, 0)
    if (typeof bitmap.close === 'function') bitmap.close()
    return canvas.toDataURL('image/jpeg', 0.92)
  } catch (e) {
    return null
  }
}

/**
 * @param {File|Blob} file
 * @returns {Promise<string>} data URL with pixels already oriented
 */
export async function fileToOrientedDataUrl(file) {
  if (!file) return null

  const mime = file.type || ''
  if (!mime.startsWith('image/')) {
    return readAsDataUrl(file)
  }

  // Prefer browser-native EXIF handling when available
  const fromBitmap = await normalizeWithCreateImageBitmap(file)
  if (fromBitmap) return fromBitmap

  // Fallback: apply EXIF Orientation tag via canvas
  try {
    const [arrayBuffer, dataUrl] = await Promise.all([
      readAsArrayBuffer(file),
      readAsDataUrl(file),
    ])
    const orientation = getExifOrientation(arrayBuffer)
    if (!orientation || orientation === 1) return dataUrl
    const img = await loadImageFromUrl(dataUrl)
    return drawOrientedImage(img, orientation)
  } catch (e) {
    return readAsDataUrl(file)
  }
}

export const _ = null
